import { api } from "@/shared/api";

/**
 * 私有化实例上的 license 管理(OEM W5-con-private)。契约源 = api
 * `com.gscq.license.web.LicenseAdminController`(main 9dec435a 起):
 *   GET  /platform/license               → LicenseView(当前判定 + license 摘要 + instanceId)
 *   POST /platform/license {license}     → 装入(整份 license.json 文本放进 license 字段,验签通过才落库)
 *   GET  /platform/license/fingerprint   → 本机指纹(签发时填进 fingerprints)
 *   POST /platform/license/heartbeat     → 立即心跳一次
 * 全部 PLATFORM_ADMIN;整段在 LicenseGuardFilter 放行清单里(只读期也能换 license)。
 *
 * ★SaaS(app.license.enabled=false)上端点照样在:GET 回 status.mode = "DISABLED"、license = null。
 *   console 据此判定「本实例是否启用 license」—— 见 isLicenseEnabled。
 */

/** DISABLED = 开关关(SaaS);OK;READONLY。 */
export type LicenseMode = "DISABLED" | "OK" | "READONLY";

/** 后端 LicenseStatus(Instant 一律 ISO 字符串)。 */
export interface LicenseStatusView {
  mode: LicenseMode | string;
  /** READONLY 原因码:NO_LICENSE / FINGERPRINT_MISMATCH / NOT_YET_VALID / EXPIRED / REVOKED / LEASE_LAPSED。 */
  reason: string | null;
  licenseId: string | null;
  partnerCode: string | null;
  deploymentId: string | null;
  expiresAt: string | null;
  trustedNow: string | null;
  /** 检测到本机时钟回拨(判定已用可信时间兜住,仅告警)。 */
  clockRollback: boolean;
  /** 租约已过、仍在宽限期内(或尚未拿到过租约)。 */
  inGrace: boolean;
  /** 若一直联系不上平台,何时进入只读。 */
  readonlyAt: string | null;
}

/** 机器指纹:三项各自 sha256 hex(小写),命中两项即认同一台机器。字段名 snake_case(@JsonProperty)。 */
export interface LicenseFingerprint {
  machine_id?: string | null;
  product_uuid?: string | null;
  mac?: string | null;
  label?: string | null;
}

/** license 被签名的原文(LicensePayload,snake_case)。 */
export interface LicensePayload {
  license_id: string;
  partner_code: string;
  deployment_id: string;
  issued_at?: string | null;
  not_before?: string | null;
  expires_at: string;
  /** null = 不限。 */
  max_tenants?: number | null;
  /** null 或含 "*" = 不限。 */
  modules?: string[] | null;
  fingerprints?: LicenseFingerprint[] | null;
  grace_hours?: number | null;
}

export interface LicenseView {
  status: LicenseStatusView;
  license: LicensePayload | null;
  instanceId: string | null;
}

const BASE = "/platform/license";

export const licenseService = {
  get: () => api.get<LicenseView>(BASE),
  upload: (licenseJson: string) => api.post<LicenseView>(BASE, { license: licenseJson }),
  fingerprint: () => api.get<LicenseFingerprint>(`${BASE}/fingerprint`),
  heartbeat: () => api.post<LicenseView>(`${BASE}/heartbeat`, null),
};
